import { useEffect, useState } from "react";
import api from "@/lib/api";
import { currentMonth, monthLabel } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, Printer, Receipt, Star, Award, CreditCard, ClipboardList, Wallet, FileSignature, UserSquare2, BookOpen, CalendarDays } from "lucide-react";
import { Kwitansi, LembarPrestasi, Sertifikat, KartuIuran, FormulirPendaftaran, ProfilSiswa, LaporanJurnal, JadwalSiswa, PAGE_SIZES } from "@/components/PrintDocs";
import { RekapKehadiran, RekapPembayaran } from "@/components/PrintReports";
import { toast } from "sonner";

const TABS = [
  { id: "formulir", label: "Formulir Pendaftaran", icon: FileSignature, size: "A4 portrait" },
  { id: "profil", label: "Profil Siswa", icon: UserSquare2, size: "A4 portrait" },
  { id: "kwitansi", label: "Kwitansi", icon: Receipt, size: "A4 portrait" },
  { id: "prestasi", label: "Lembar Prestasi", icon: Star, size: "A4 portrait" },
  { id: "sertifikat", label: "Sertifikat", icon: Award, size: "A4 landscape" },
  { id: "iuran", label: "Kartu Iuran", icon: CreditCard, size: "A4 portrait" },
  { id: "laporan-jurnal", label: "Laporan Jurnal", icon: BookOpen, size: "A4 portrait" },
  { id: "jadwal", label: "Jadwal Siswa", icon: CalendarDays, size: "A4 portrait" },
  { id: "rekap-kehadiran", label: "Rekap Kehadiran", icon: ClipboardList, size: "A4 portrait" },
  { id: "rekap-pembayaran", label: "Rekap Pembayaran", icon: Wallet, size: "A4 portrait" },
];
const REKAP_TABS = ["rekap-kehadiran", "rekap-pembayaran"];
const MONTH_TABS = ["kwitansi", "laporan-jurnal", ...REKAP_TABS];

export default function Documents() {
  const [settings, setSettings] = useState(null);
  const [students, setStudents] = useState([]);
  const [tab, setTab] = useState("formulir");
  const [studentId, setStudentId] = useState("");
  const [month, setMonth] = useState(currentMonth());
  const [year, setYear] = useState(new Date().getFullYear());
  const [data, setData] = useState({ journals: [], payments: [] });
  const [loading, setLoading] = useState(false);
  const [report, setReport] = useState(null);
  const isRekap = REKAP_TABS.includes(tab);
  const page = PAGE_SIZES[tab];

  useEffect(() => {
    if (!isRekap) return;
    setLoading(true);
    api.get("/reports/monthly", { params: { month } }).then((r) => setReport(r.data)).finally(() => setLoading(false));
  }, [isRekap, month]);

  useEffect(() => {
    api.get("/settings").then((r) => setSettings(r.data));
    api.get("/students").then((r) => { setStudents(r.data); if (r.data[0]) setStudentId(r.data[0].id); });
  }, []);

  useEffect(() => {
    if (!studentId) return;
    setLoading(true);
    Promise.all([api.get("/journals", { params: { student_id: studentId } }), api.get("/payments", { params: { student_id: studentId } })])
      .then(([j, p]) => setData({ journals: j.data, payments: p.data })).finally(() => setLoading(false));
  }, [studentId]);

  const student = students.find((s) => s.id === studentId);
  const payment = data.payments.find((p) => p.month === month);
  const yearPayments = data.payments.filter((p) => (p.month || "").startsWith(String(year)));
  const monthJournals = data.journals.filter((j) => (j.date || "").startsWith(month)).sort((a, b) => a.date.localeCompare(b.date));

  const doPrint = () => {
    if (tab === "kwitansi" && !payment) return toast.error(`Belum ada data pembayaran ${monthLabel(month)} untuk siswa ini`);
    window.print();
  };

  return (
    <div className="space-y-6 animate-float-in">
      <style>{`@media print { @page { size: ${page.w}mm ${page.h}mm; margin: 0; } }`}</style>
      <div>
        <h1 className="font-display text-3xl font-bold text-slate-900">Dokumen & Cetak PDF</h1>
        <p className="text-slate-500 mt-1">Pilih dokumen, siswa, lalu Cetak / Simpan sebagai PDF · ukuran kertas diatur otomatis</p>
      </div>

      <div className="no-print space-y-4">
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="rounded-xl bg-amber-50 flex-wrap h-auto justify-start">
            {TABS.map((t) => (
              <TabsTrigger key={t.id} value={t.id} data-testid={`doc-tab-${t.id}`} className="rounded-lg data-[state=active]:bg-white">
                <t.icon className="w-4 h-4 mr-1" /> {t.label}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>

        <div className="flex flex-wrap gap-3 items-end">
          {!isRekap && tab !== "formulir" && (
            <div className="min-w-[200px]">
              <Label className="text-xs text-slate-400">Siswa</Label>
              <Select value={studentId} onValueChange={setStudentId}>
                <SelectTrigger data-testid="doc-student-select" className="rounded-xl border-amber-200 mt-1"><SelectValue placeholder="Pilih siswa" /></SelectTrigger>
                <SelectContent>{students.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}{s.status !== "aktif" ? " (nonaktif)" : ""}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          )}
          {MONTH_TABS.includes(tab) && (
            <div><Label className="text-xs text-slate-400">Bulan</Label><Input data-testid="doc-month-input" type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="rounded-xl border-amber-200 w-44 mt-1" /></div>
          )}
          {tab === "iuran" && (
            <div><Label className="text-xs text-slate-400">Tahun</Label><Input type="number" value={year} onChange={(e) => setYear(e.target.value)} className="rounded-xl border-amber-200 w-32 mt-1" /></div>
          )}
          <Button data-testid="print-doc-btn" onClick={doPrint} className="rounded-xl bg-orange-500 hover:bg-orange-600">
            <Printer className="w-4 h-4 mr-1" /> Cetak / Simpan PDF
          </Button>
          <span data-testid="doc-page-size" className="text-xs text-slate-400 pb-2.5">Kertas: {TABS.find((t) => t.id === tab)?.size}</span>
        </div>
      </div>

      {loading ? <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-orange-500" /></div> : (
        <Card className="p-4 sm:p-6 rounded-2xl border-amber-100 bg-slate-100/60 overflow-x-auto gemma-scroll">
          <div id="print-root">
            {tab === "formulir" && <FormulirPendaftaran settings={settings} />}
            {student && tab === "profil" && <ProfilSiswa settings={settings} student={student} journals={data.journals} payments={data.payments} />}
            {student && tab === "kwitansi" && <Kwitansi settings={settings} student={student} payment={payment} />}
            {student && tab === "prestasi" && <LembarPrestasi settings={settings} student={student} journals={data.journals} />}
            {student && tab === "sertifikat" && <Sertifikat settings={settings} student={student} />}
            {student && tab === "iuran" && <KartuIuran settings={settings} student={student} payments={yearPayments} year={year} />}
            {student && tab === "laporan-jurnal" && <LaporanJurnal settings={settings} student={student} journals={monthJournals} month={month} />}
            {student && tab === "jadwal" && <JadwalSiswa settings={settings} student={student} />}
            {tab === "rekap-kehadiran" && <RekapKehadiran settings={settings} report={report} />}
            {tab === "rekap-pembayaran" && <RekapPembayaran settings={settings} report={report} />}
          </div>
        </Card>
      )}
    </div>
  );
}
